/**
 * Shared pack-selection composable — the FIRST step of both creation flows (campaign creation in
 * `CampaignsDashboard`, character creation at `/builder`). INC-D of `multi-pack-runtime` (ADR-066).
 *
 * `selectPack()` owns the **single-pack auto-skip** (SPEC §4.4): when exactly one entry is visible
 * (a `VITE_PACKS` white-label build, or a restricted pack the account holds no grant for filtered
 * out) it resolves that pack immediately and the modal never mounts; otherwise it opens `PackPicker`
 * and waits for a choice. On confirm it **preloads** the chosen pack (SPEC §4.2) so every downstream
 * synchronous `getResolvedUi/Data` read succeeds, then resolves the id (or `null` on cancel). The
 * host renders `<PackPicker v-if="isOpen" …>` and wires `@select`/`@cancel` to `onSelect`/`onCancel`.
 * With no visible pack it resolves `null` without opening the picker; the host shows `packPicker.none`
 * instead.
 */
import { inject, ref, type InjectionKey } from 'vue'
import {
  packRegistry,
  RESTRICTED_PACK_IDS,
  type PackId,
  type PackRegistryEntry,
} from '@/content-packs/registry'
import { preloadPack } from '@/content-packs/resolved'
import { hasPackGrant } from '@/composables/useAccess'

/** ADR-099 D5: a registered pack is visible unless it is restricted and the account holds no grant
 *  for it. No role substitutes for the grant. */
export function isPackVisible(id: unknown): boolean {
  return (
    typeof id === 'string' &&
    packRegistry.has(id) &&
    (!RESTRICTED_PACK_IDS.includes(id) || hasPackGrant(id))
  )
}

/** Validate an untrusted value (route query, persisted `pack_id`, `_contentPackId`) against the
 *  registry of shippable packs. Returns the `PackId` only if it is a registered, filter-visible pack
 *  the account may use (ADR-099); otherwise `null` so callers can fall back to the tooling default. */
export function resolveDraftPack(value: unknown): PackId | null {
  return isPackVisible(value) ? (value as PackId) : null
}

/**
 * Injection key for the builder draft's pack. `BuilderEntry`/`CharacterWizard` provide the resolved
 * pack; the draft-flow child components (`StepNav`, `BuilderToolbar`, `GenericChoiceStep`,
 * `CharacterNameInput`, …) inject it so they thread the SAME pack into their `useCharacterStore`
 * (ignored after first creation) and `useChoicePicker` (resolved per pack) calls.
 */
export const DRAFT_PACK_KEY: InjectionKey<PackId> = Symbol('draftPack')

/** The current builder draft's pack, from the nearest provider. Falls back to the tooling default
 *  (dnd-4.5) outside a builder tree (e.g. tests mounting a step component in isolation). */
export function useProvidedDraftPack(): PackId {
  return inject(DRAFT_PACK_KEY, 'dnd-4.5-dab')
}

export function usePackSelection() {
  const packs: PackRegistryEntry[] = [...packRegistry.values()].filter((p) => isPackVisible(p.id))
  const isOpen = ref(false)
  const isLoading = ref(false)
  let resolver: ((id: PackId | null) => void) | null = null

  /** Resolve a chosen, PRELOADED pack id — or `null` if the user cancels. Auto-skips the modal for
   *  a single-pack registry. `isLoading` covers the (usually cache-warm) preload. */
  async function selectPack(): Promise<PackId | null> {
    if (packs.length === 0) return null // never open an empty picker; hosts show packPicker.none
    if (packs.length === 1) {
      const only = packs[0]!.id
      await preloadPack(only)
      return only
    }
    const chosen = await new Promise<PackId | null>((resolve) => {
      resolver = resolve
      isOpen.value = true
    })
    if (!chosen) return null
    isLoading.value = true
    try {
      await preloadPack(chosen)
    } finally {
      isLoading.value = false
    }
    return chosen
  }

  function onSelect(id: PackId) {
    isOpen.value = false
    resolver?.(id)
    resolver = null
  }

  function onCancel() {
    isOpen.value = false
    resolver?.(null)
    resolver = null
  }

  return { packs, isOpen, isLoading, selectPack, onSelect, onCancel }
}
